const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3011/api';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.token = null;
  }

  // Set authentication token
  setToken(token) {
    this.token = token;
  }

  // Remove authentication token
  removeToken() {
    this.token = null;
  }

  async request(endpoint, options = {}, retryCount = 0) {
    const url = `${this.baseURL}${endpoint}`;

    // Get token from storage if not set in instance
    if (!this.token) {
      // Try secureStorage format first
      try {
        const itemStr = localStorage.getItem('auth_token');
        if (itemStr) {
          const item = JSON.parse(itemStr);
          if (item.value && Date.now() <= item.expiry) {
            this.token = item.value.startsWith('"') ? JSON.parse(item.value) : item.value;
          }
        }
      } catch (error) {
        // Try direct token storage
        this.token = localStorage.getItem('auth_token');
      }

      // Fallback to simple token storage
      if (!this.token) {
        this.token = localStorage.getItem('token');
      }
    }

    const requestConfig = {
      headers: {
        'Accept': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` }),
      },
      ...options,
    };

    // Set Content-Type only if not FormData
    if (!options.isFormData && !(options.body instanceof FormData)) {
      requestConfig.headers['Content-Type'] = 'application/json';
    }

    // Merge headers if provided in options
    if (options.headers) {
      requestConfig.headers = { ...requestConfig.headers, ...options.headers };
    }

    try {
      const response = await fetch(url, requestConfig);

      // Handle 401 Unauthorized
      if (response.status === 401 && retryCount === 0) {
        // Don't try to refresh token for login requests
        if (endpoint === '/auth/login') {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || errorData.message || 'Invalid credentials');
        }
        
        // For other requests, try to refresh token
        try {
          const authService = await import('../services/auth');
          await authService.default.refreshAuthToken();
          
          this.token = authService.default.token;
          return this.request(endpoint, options, 1);
        } catch (refreshError) {
          const authService = await import('../services/auth');
          authService.default.clearAuthData();
          throw new Error('Session expired. Please login again.');
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      if (response.status === 204) {
        return {};
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Dashboard APIs
  async getDashboardStats(countryId = null) {
    const params = countryId ? `?country_id=${countryId}` : '';
    return this.request(`/dashboard/stats${params}`);
  }

  async getDashboardCountries() {
    return this.request('/dashboard/countries');
  }

  async getRecentActivities(limit = 10) {
    return this.request(`/dashboard/recent-activities?limit=${limit}`);
  }

  async getRecentEnquiries(limit = 5) {
    return this.request(`/dashboard/recent-enquiries?limit=${limit}`);
  }

  // Referral Partners APIs
  async getReferralPartners(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/referral-partners${query ? `?${query}` : ''}`);
  }

  async createReferralPartner(data) {
    return this.request('/referral-partners', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateReferralPartner(id, data) {
    return this.request(`/referral-partners/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getReferralPartnerById(id) {
    return this.request(`/referral-partners/${id}`);
  }

  async getReferralPartnerStats(partnerId, timeRange = '7d') {
    return this.request(`/referral-partners/${partnerId}/stats?timeRange=${timeRange}`);
  }

  async getReferralPartnerApplications(partnerId, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/referral-partners/${partnerId}/applications${query ? `?${query}` : ''}`);
  }

  async deleteReferralPartner(id) {
    return this.request(`/referral-partners/${id}`, {
      method: 'DELETE',
    });
  }

  // Team Leads APIs
  async getTeamLeads(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/team-leads${query ? `?${query}` : ''}`);
  }

  async createTeamLead(data) {
    return this.request('/team-leads', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTeamLead(id, data) {
    return this.request(`/team-leads/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteTeamLead(id) {
    return this.request(`/team-leads/${id}`, {
      method: 'DELETE',
    });
  }

  // Admissions APIs
  async getAdmissions(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/admissions${query ? `?${query}` : ''}`);
  }

  // Public admission submission (no auth required)
  async submitPublicAdmission(data, referralPartnerId = null) {
    const endpoint = referralPartnerId
      ? `/admissions/submit/${referralPartnerId}`
      : '/admissions/submit';

    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async deleteAdmission(id) {
    return this.request(`/admissions/${id}`, {
      method: 'DELETE',
    });
  }

  async allocateAdmission(id, referralPartnerId) {
    return this.request(`/admissions/${id}/allocate`, {
      method: 'PUT',
      body: JSON.stringify({ referral_partner_id: referralPartnerId }),
    });
  }

  async updateApplicationStatus(id, status, remarks = '') {
    return this.request(`/admissions/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status, remarks }),
    });
  }



  // Enquiries APIs
  async getEnquiries(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/enquiries${query ? `?${query}` : ''}`);
  }

  async createEnquiry(data) {
    try {
      const response = await fetch(`${this.baseURL}/enquiries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async deleteEnquiry(id) {
    return this.request(`/enquiries/${id}`, {
      method: 'DELETE',
    });
  }

  // Universities APIs
  async getUniversities(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/universities${query ? `?${query}` : ''}`);
  }

  async createUniversity(data) {
    return this.request('/universities', {
      method: 'POST',
      body: data,
      isFormData: true
    });
  }

  async updateUniversity(id, data) {
    return this.request(`/universities/${id}`, {
      method: 'PUT',
      body: data,
      isFormData: true
    });
  }

  async deleteUniversity(id) {
    return this.request(`/universities/${id}`, {
      method: 'DELETE',
    });
  }

  // Testimonials APIs
  async getTestimonials(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/testimonials${query ? `?${query}` : ''}`);
  }

  async createTestimonial(data) {
    return this.request('/testimonials', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTestimonial(id, data) {
    return this.request(`/testimonials/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteTestimonial(id) {
    return this.request(`/testimonials/${id}`, {
      method: 'DELETE',
    });
  }

  // SEO APIs
  async getSEOSettings(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/seo${query ? `?${query}` : ''}`);
  }

  async createSEOSetting(data) {
    return this.request('/seo', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSEOSetting(id, data) {
    return this.request(`/seo/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSEOSetting(id) {
    return this.request(`/seo/${id}`, {
      method: 'DELETE',
    });
  }



  // Task Management APIs
  async getTasks(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/tasks${query ? `?${query}` : ''}`);
  }

  async createTask(data) {
    return this.request('/tasks', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTask(id, data) {
    return this.request(`/tasks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteTask(id) {
    return this.request(`/tasks/${id}`, {
      method: 'DELETE',
    });
  }

  async assignTask(taskId, userId) {
    return this.request(`/tasks/${taskId}/assign`, {
      method: 'PUT',
      body: JSON.stringify({ userId }),
    });
  }

  async assignTaskByEmail(taskId, assigneeEmail) {
    return this.request(`/tasks/${taskId}/assign`, {
      method: 'PUT',
      body: JSON.stringify({ assignee_email: assigneeEmail }),
    });
  }

  async updateTaskStatus(taskId, status) {
    return this.request(`/tasks/${taskId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  async updateTaskTemperature(taskId, temperature) {
    return this.request(`/tasks/${taskId}/temperature`, {
      method: 'PUT',
      body: JSON.stringify({ temperature }),
    });
  }

  async moveTaskToInbound(taskId) {
    return this.request(`/tasks/${taskId}/move-to-inbound`, {
      method: 'PUT',
    });
  }

  // User Management APIs
  async getUsers(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/users${query ? `?${query}` : ''}`);
  }

  // Authentication APIs
  async login(credentials) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async logout() {
    return this.request('/auth/logout', {
      method: 'POST',
    });
  }

  async refreshToken() {
    // Don't use the main request method to avoid infinite recursion
    const url = `${this.baseURL}/auth/refresh`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || 'Token refresh failed');
    }

    return await response.json();
  }

  async getProfile() {
    return this.request('/auth/profile');
  }

  async changePassword(data) {
    return this.request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Gallery APIs
  async getGalleries(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/gallery${query ? `?${query}` : ''}`);
  }

  async createGallery(data) {
    return this.request('/gallery', {
      method: 'POST',
      body: data,
      isFormData: true
    });
  }

  async updateGallery(id, data) {
    return this.request(`/gallery/${id}`, {
      method: 'PUT',
      body: data,
      isFormData: true
    });
  }

  async deleteGallery(id) {
    return this.request(`/gallery/${id}`, {
      method: 'DELETE',
    });
  }

  // Blog APIs
  async getBlogs(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/blogs${query ? `?${query}` : ''}`);
  }

  async getBlog(id) {
    return this.request(`/blogs/${id}`);
  }

  async createBlog(data) {
    return this.request('/blogs', {
      method: 'POST',
      body: data,
      isFormData: true
    });
  }

  async updateBlog(id, data) {
    return this.request(`/blogs/${id}`, {
      method: 'PUT',
      body: data,
      isFormData: true
    });
  }

  async deleteBlog(id) {
    return this.request(`/blogs/${id}`, {
      method: 'DELETE',
    });
  }

  // News APIs
  async getNews(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/news${query ? `?${query}` : ''}`);
  }

  async getNewsById(id) {
    return this.request(`/news/${id}`);
  }

  async createNews(data) {
    return this.request('/news', {
      method: 'POST',
      body: data,
      isFormData: true
    });
  }

  async updateNews(id, data) {
    return this.request(`/news/${id}`, {
      method: 'PUT',
      body: data,
      isFormData: true
    });
  }

  async deleteNews(id) {
    return this.request(`/news/${id}`, {
      method: 'DELETE',
    });
  }

  // Feature APIs
  async getFeatures(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/features${query ? `?${query}` : ''}`);
  }

  async createFeature(data) {
    return this.request('/features', {
      method: 'POST',
      body: data,
      isFormData: true
    });
  }

  async updateFeature(id, data) {
    return this.request(`/features/${id}`, {
      method: 'PUT',
      body: data,
      isFormData: true
    });
  }

  async deleteFeature(id) {
    return this.request(`/features/${id}`, {
      method: 'DELETE',
    });
  }

  // Countries APIs
  async getCountries(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/countries${query ? `?${query}` : ''}`);
  }

  async createCountry(data) {
    return this.request('/countries', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCountry(id, data) {
    return this.request(`/countries/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCountry(id) {
    return this.request(`/countries/${id}`, {
      method: 'DELETE',
    });
  }

  // Courses APIs
  async getCourses(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/courses${query ? `?${query}` : ''}`);
  }

  async createCourse(data) {
    return this.request('/courses', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCourse(id, data) {
    return this.request(`/courses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCourse(id) {
    return this.request(`/courses/${id}`, {
      method: 'DELETE',
    });
  }

  // University Countries APIs
  async getUniversityCountries(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/university-countries${query ? `?${query}` : ''}`);
  }

  async createUniversityCountry(data) {
    return this.request('/university-countries', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUniversityCountry(id, data) {
    return this.request(`/university-countries/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteUniversityCountry(id) {
    return this.request(`/university-countries/${id}`, {
      method: 'DELETE',
    });
  }

  // Document APIs
  async uploadDocument(data) {
    return this.request('/documents/upload', {
      method: 'POST',
      body: data,
      isFormData: true
    });
  }

  async verifyDocument(id, data) {
    return this.request(`/documents/verify/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getPendingDocuments() {
    return this.request('/documents/pending');
  }

  // Accounts Manager APIs
  async getAccountsRecords() {
    return this.request('/accounts/records');
  }

  async updateAccountsRecord(id, data) {
    return this.request(`/accounts/records/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Generic HTTP methods
  async get(endpoint) {
    return this.request(endpoint);
  }

  async post(endpoint, data, options = {}) {
    const requestOptions = {
      method: 'POST',
      ...options
    };

    if (data instanceof FormData) {
      requestOptions.body = data;
      // For FormData, signal to not set Content-Type in request method
      requestOptions.isFormData = true;
    } else {
      requestOptions.body = JSON.stringify(data);
    }

    return this.request(endpoint, requestOptions);
  }

  async put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint) {
    return this.request(endpoint, {
      method: 'DELETE',
    });
  }
}

const apiService = new ApiService();
export default apiService;